import { Lang, Team, TeamAPI } from "@brg/api";
import { Colors, FlexBox } from "@brg/ui";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DropdownContainer, ListItem, OtherMediaWrapper, SvgWrapper, TabContainer, TabItem } from "../styles";
import { useHistory } from "react-router-dom";
import { ApprovedIcon } from "shared/icons/ApprovedIcon";
import { OPTION_TYPE } from "@brg/ui/dist/types/ui-input/types";
import { ClickOutsideComponent } from "shared/components/CommentsController/components/ClickOutside";
import { isTeamChannel, ModerationType } from "shared/pages/teams/const/ModerationTypes";
import { ACTIVE_ARCHIVE_TEAM_STATUS, DRAFT_TEAM_STATUS, PUBLISHED_TEAM_STATUS } from "shared/helpers/teamsStatus";

enum TABS {
    active = 'active',
    archive = 'archive'
}

const ChannelsDropdown: FC <{ channel: any }> = ({channel}) => {
    const [tab, setTab] = useState(TABS.active);
    const history = useHistory();
    const fetchDataRef = useRef<any>(null)
    const [visible, setVisible] = useState(false);
    const { t, i18n: { language } } = useTranslation();
    const [channels, setChannels] = useState<Team[]>();

    const { isChannel } = isTeamChannel(channel?.moderationType)

    const isActive = (id: string) => {
        return channel?.teamId === id
    }

    const owner = useMemo(() => {
        return channel?.owners?.[0]
    } , [channel])

    const fetchChannels = async (id: string) => {
        fetchDataRef.current = true;
        await TeamAPI.GetList({
            moderationTypes: [ModerationType.Channel, ModerationType.ChannelGroupOrCourse], 
            ownerIds: [id],
            statuses: [
                ACTIVE_ARCHIVE_TEAM_STATUS,
                DRAFT_TEAM_STATUS,
                PUBLISHED_TEAM_STATUS,
            ] 
        })
        .then((response) => response.json())
        .then((response) => {
            setChannels(response?.data)
        })
    }

    useEffect(() => {
        if (!fetchDataRef.current && owner) {
            fetchChannels(owner?.accountId)
        }
    } , [fetchDataRef, owner])

    const sortItems = (array: OPTION_TYPE[], language = 'ru') => {
        return [...array].sort((a, b) => {
            if (a.value === channel?.teamId) return -1;
            if (b.value === channel?.teamId) return 1;
            return a.label.localeCompare(b.label, language, { sensitivity: 'base' })
        });
    };

    const sortedItems = useMemo(() => {
        return channels?.reduce((acc: any, item: any) => {
            if (item.status === PUBLISHED_TEAM_STATUS) {
              return { ...acc, active: [...acc?.active, item] };
            } else if (item.status === ACTIVE_ARCHIVE_TEAM_STATUS) {
              return { ...acc, archive: [...acc?.archive, item] };
            }
            return acc;
          }, { active: [], archive: [] });
    } , [tab, channels]) 

    const isAutoChannel = useMemo(() => {
        return channel?.moderationType === ModerationType.ChannelGroupOrCourse && channel?.owners?.length > 1
    } , [channel])

    const computeItems = useMemo(() => {
        return sortedItems?.[tab]
    } , [tab, sortedItems]) 
    

    const tabs = useMemo(() => {
        return (
            <TabContainer>
                {Object.keys(TABS)?.map((item) => {
                    return (
                        <TabItem
                            hidden={!sortedItems?.[item]?.length} 
                            isActive={tab === item} 
                            onClick={() => setTab(TABS?.[item as keyof {}])}
                        >
                            {t(`subscription.${item}`)}
                        </TabItem>
                    )
                })}
            </TabContainer>
        )
    } , [tab, sortedItems, language])

    const title = useMemo(() => (
        <OtherMediaWrapper 
            isOpen={visible} 
            direction="row" 
            onClick={() => {
                setVisible(!visible)
            }}
        >
            <div>{t('subscription.other_media')}</div>
            <FlexBox>
                <svg width="16" height="16" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                d="M6 8.36851L1.69486 3.9176C1.47871 3.69413 1.12826 3.69413 0.912111 3.91761C0.695961 4.14108 0.695963 4.5034 0.912115 4.72687L5.60863 9.5824C5.82478 9.80587 6.17522 9.80587 6.39137 9.5824L11.0879 4.72687C11.304 4.5034 11.304 4.14108 11.0879 3.91761C10.8717 3.69413 10.5213 3.69413 10.3051 3.9176L6 8.36851Z"
                fill={'#909090'}/>
                </svg>
            </FlexBox>
        </OtherMediaWrapper>
    ) , [visible, language])

    const options = useMemo(() => {
        if (!!computeItems?.length) {
            const baseChannels = computeItems?.map((channel) => {
                return {
                    label: Lang(channel.name, language),
                    value: channel?.teamId
                }
            })
    
            return sortItems(baseChannels, language)
        }
    } , [computeItems, language])

    const dropdown = useMemo(() => {
        if (!visible) return null;

        return (
            <DropdownContainer>
                {tabs}
                <>
                    {options?.map((channel) => {
                        return (
                        <ListItem
                            key={channel?.value}
                                disable={isActive?.(channel.value)} 
                                onClick={() => {
                                if (isActive?.(channel.value)) return;
                                history.push(channel.value)
                            }}>
                                <div>{channel?.label}</div>
                                <SvgWrapper>
                                    <ApprovedIcon 
                                        fill={Colors.galBlack100} 
                                        display={!!isActive?.(channel.value)}
                                    />
                                </SvgWrapper>
                        </ListItem>
                        )
                    })}
                </>
            </DropdownContainer>
        )
    } , [computeItems, visible])

    if (!channels?.length || !isChannel || channels?.length === 1 || isAutoChannel) {
        return null;
    }

    return (
        <FlexBox
            offsetTop={8} 
            offsetBottom={2}
            direction="column" 
            style={{ 
                position: 'relative',  
            }}
        >
            <ClickOutsideComponent 
                onClose={() => {
                    setVisible(false)
                }}
            >
                {title}
                {dropdown}
            </ClickOutsideComponent>
        </FlexBox>
    )
}

export default ChannelsDropdown;import { Lang, Team, TeamAPI } from "@brg/api";
import { Colors, FlexBox } from "@brg/ui";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DropdownContainer, ListItem, OtherMediaWrapper, SvgWrapper, TabContainer, TabItem } from "../styles";
import { useHistory } from "react-router-dom";
import { ApprovedIcon } from "shared/icons/ApprovedIcon";
import { OPTION_TYPE } from "@brg/ui/dist/types/ui-input/types";
import { ClickOutsideComponent } from "shared/components/CommentsController/components/ClickOutside";
import { isTeamChannel, ModerationType } from "shared/pages/teams/const/ModerationTypes";
import { ACTIVE_ARCHIVE_TEAM_STATUS, DRAFT_TEAM_STATUS, PUBLISHED_TEAM_STATUS } from "shared/helpers/teamsStatus";

enum TABS {
    active = 'active',
    archive = 'archive'
}

const ChannelsDropdown: FC <{ channel: any }> = ({channel}) => {
    const [tab, setTab] = useState(TABS.active);
    const history = useHistory();
    const fetchDataRef = useRef<any>(null)
    const [visible, setVisible] = useState(false);
    const { t, i18n: { language } } = useTranslation();
    const [channels, setChannels] = useState<Team[]>();

    const { isChannel } = isTeamChannel(channel?.moderationType)

    const isActive = (id: string) => {
        return channel?.teamId === id
    }

    const owner = useMemo(() => {
        return channel?.owners?.[0]
    } , [channel])

    const fetchChannels = async (id: string) => {
        fetchDataRef.current = true;
        await TeamAPI.GetList({
            moderationTypes: [ModerationType.Channel, ModerationType.ChannelGroupOrCourse], 
            ownerIds: [id],
            statuses: [
                ACTIVE_ARCHIVE_TEAM_STATUS,
                DRAFT_TEAM_STATUS,
                PUBLISHED_TEAM_STATUS,
            ] 
        })
        .then((response) => response.json())
        .then((response) => {
            setChannels(response?.data)
        })
    }

    useEffect(() => {
        if (!fetchDataRef.current && owner) {
            fetchChannels(owner?.accountId)
        }
    } , [fetchDataRef, owner])

    const sortItems = (array: OPTION_TYPE[], language = 'ru') => {
        return [...array].sort((a, b) => {
            if (a.value === channel?.teamId) return -1;
            if (b.value === channel?.teamId) return 1;
            return a.label.localeCompare(b.label, language, { sensitivity: 'base' })
        });
    };

    const sortedItems = useMemo(() => {
        return channels?.reduce((acc: any, item: any) => {
            if (item.status === PUBLISHED_TEAM_STATUS) {
              return { ...acc, active: [...acc?.active, item] };
            } else if (item.status === ACTIVE_ARCHIVE_TEAM_STATUS) {
              return { ...acc, archive: [...acc?.archive, item] };
            }
            return acc;
          }, { active: [], archive: [] });
    } , [tab, channels]) 

    const isAutoChannel = useMemo(() => {
        return channel?.moderationType === ModerationType.ChannelGroupOrCourse && channel?.owners?.length > 1
    } , [channel])

    const computeItems = useMemo(() => {
        return sortedItems?.[tab]
    } , [tab, sortedItems]) 
    

    const tabs = useMemo(() => {
        return (
            <TabContainer>
                {Object.keys(TABS)?.map((item) => {
                    return (
                        <TabItem
                            hidden={!sortedItems?.[item]?.length} 
                            isActive={tab === item} 
                            onClick={() => setTab(TABS?.[item as keyof {}])}
                        >
                            {t(`subscription.${item}`)}
                        </TabItem>
                    )
                })}
            </TabContainer>
        )
    } , [tab, sortedItems, language])

    const title = useMemo(() => (
        <OtherMediaWrapper 
            isOpen={visible} 
            direction="row" 
            onClick={() => {
                setVisible(!visible)
            }}
        >
            <div>{t('subscription.other_media')}</div>
            <FlexBox>
                <svg width="16" height="16" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                d="M6 8.36851L1.69486 3.9176C1.47871 3.69413 1.12826 3.69413 0.912111 3.91761C0.695961 4.14108 0.695963 4.5034 0.912115 4.72687L5.60863 9.5824C5.82478 9.80587 6.17522 9.80587 6.39137 9.5824L11.0879 4.72687C11.304 4.5034 11.304 4.14108 11.0879 3.91761C10.8717 3.69413 10.5213 3.69413 10.3051 3.9176L6 8.36851Z"
                fill={'#909090'}/>
                </svg>
            </FlexBox>
        </OtherMediaWrapper>
    ) , [visible, language])

    const options = useMemo(() => {
        if (!!computeItems?.length) {
            const baseChannels = computeItems?.map((channel) => {
                return {
                    label: Lang(channel.name, language),
                    value: channel?.teamId
                }
            })
    
            return sortItems(baseChannels, language)
        }
    } , [computeItems, language])

    const dropdown = useMemo(() => {
        if (!visible) return null;

        return (
            <DropdownContainer>
                {tabs}
                <>
                    {options?.map((channel) => {
                        return (
                        <ListItem
                            key={channel?.value}
                                disable={isActive?.(channel.value)} 
                                onClick={() => {
                                if (isActive?.(channel.value)) return;
                                history.push(channel.value)
                            }}>
                                <div>{channel?.label}</div>
                                <SvgWrapper>
                                    <ApprovedIcon 
                                        fill={Colors.galBlack100} 
                                        display={!!isActive?.(channel.value)}
                                    />
                                </SvgWrapper>
                        </ListItem>
                        )
                    })}
                </>
            </DropdownContainer>
        )
    } , [computeItems, visible])

    if (!channels?.length || !isChannel || channels?.length === 1 || isAutoChannel) {
        return null;
    }

    return (
        <FlexBox
            offsetTop={8} 
            offsetBottom={2}
            direction="column" 
            style={{ 
                position: 'relative',  
            }}
        >
            <ClickOutsideComponent 
                onClose={() => {
                    setVisible(false)
                }}
            >
                {title}
                {dropdown}
            </ClickOutsideComponent>
        </FlexBox>
    )
}

export default ChannelsDropdown;